'use client'

import React, { useState, useEffect } from 'react'
import { Search, Loader2, AlertCircle } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import ProductListItem from './ProductListItem'
import InventoryTerminalDisplay from './InventoryTerminalDisplay'
import { siloApi } from '@/lib/api/silo-api'
import type { InventoryItem } from '@/lib/api/silo-api/types'

interface InventoryBrowserSectionProps {
  onAddProduct?: (product: InventoryItem) => void
  shouldFetch?: boolean
}

export default function InventoryBrowserSection({ onAddProduct, shouldFetch = true }: InventoryBrowserSectionProps) {
  const [products, setProducts] = useState<InventoryItem[]>([])
  const [filteredProducts, setFilteredProducts] = useState<InventoryItem[]>([])
  const [selectedProduct, setSelectedProduct] = useState<InventoryItem | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  // ✅ OPTIMIZED: Fetch only when shouldFetch is true, with proper loading states
  useEffect(() => {
    if (status !== 'idle' || !shouldFetch) return

    const fetchWithMinLoading = async () => {
      setStatus('loading')
      setError(null)
      const startTime = Date.now()

      try {
        // Use public endpoint - no authentication required
        const response = await fetch('/api/public/inventory')

        if (!response.ok) {
          throw new Error(`Failed to fetch products: ${response.status}`)
        }

        const data = await response.json()
        console.log('Public products response:', data)

        // The public endpoint returns { items: [...] }
        const productList = data.items || []

        // ✅ Ensure minimum 300ms loading time to prevent flash
        const elapsed = Date.now() - startTime
        if (elapsed < 300) {
          await new Promise(resolve => setTimeout(resolve, 300 - elapsed))
        }

        setProducts(productList)
        setFilteredProducts(productList)

        // Auto-select first product if available
        if (productList.length > 0) {
          setSelectedProduct(productList[0])
        }

        setStatus('success')
      } catch (err: any) {
        console.error('Failed to fetch products:', err)
        setError('Failed to load products. Please try again.')
        setStatus('error')
      }
    }

    fetchWithMinLoading()
  }, [status, shouldFetch])

  // Filter products based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProducts(products)
      return
    }

    const filtered = products.filter(product =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.product_type?.name?.toLowerCase().includes(searchQuery.toLowerCase())
    )

    setFilteredProducts(filtered)
  }, [searchQuery, products])

  // ✅ OPTIMIZED: Skeleton loading state with glassmorphism design
  if (status === 'loading') {
    return (
      <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 lg:gap-6 w-full overflow-hidden">
        {/* Left Panel - Skeleton for product list */}
        <div className="flex flex-col min-w-0">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-2">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" data-testid="skeleton-loader" />
            ))}
          </div>
        </div>
        {/* Right Panel - Skeleton for terminal */}
        <div className="min-w-0 overflow-hidden">
          <Skeleton className="h-[400px] w-full" />
        </div>
      </div>
    )
  }

  // ✅ OPTIMIZED: Error state with retry button
  if (status === 'error') {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <Alert className="bg-red-500/10 border border-red-500/20 max-w-md">
          <AlertCircle className="h-4 w-4 text-red-400" />
          <AlertDescription className="text-red-300 mb-3">
            {error}
          </AlertDescription>
          <Button
            onClick={() => setStatus('idle')}
            variant="outline"
            size="sm"
            className="border-white/20 hover:border-white/30 text-white"
          >
            Retry
          </Button>
        </Alert>
      </div>
    )
  }

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 lg:gap-6 w-full overflow-hidden">
      {/* Left Panel - Product List */}
      <div className="flex flex-col min-w-0">
        {/* Header with Search */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="text-lg sm:text-xl font-semibold text-hierarchy-1 truncate">
              Browse Products
            </h3>
            {/* Results count */}
            <p className="text-xs sm:text-sm text-white/60 whitespace-nowrap">
              {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'} found
            </p>
          </div>
          
          <div className="relative pl-1 pr-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-800/30 border-white/10 text-white placeholder:text-white/40 ring-offset-0 focus:ring-2 focus:ring-blue-400/60 focus:outline-none"
            />
          </div>
        </div>

        {/* Products List - Fixed height with scroll (smaller on mobile) */}
        <div className="max-h-[200px] sm:max-h-[300px] lg:max-h-[400px] scroll-safe space-y-2 pr-2">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-white/60">
                {searchQuery ? 'No products match your search.' : 'No products available.'}
              </p>
            </div>
          ) : (
            filteredProducts.map((product) => (
              <ProductListItem
                key={product.id}
                product={product}
                isSelected={selectedProduct?.id === product.id}
                onSelect={setSelectedProduct}
                onAddProduct={onAddProduct}
              />
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Terminal Display */}
      <div className="min-w-0 overflow-hidden">
        <InventoryTerminalDisplay 
          selectedProduct={selectedProduct} 
          onAddProduct={onAddProduct}
        />
      </div>
    </div>
  )
}